import React from "react"
import PropTypes from "prop-types"
import { IntlContextConsumer } from "gatsby-plugin-react-intl"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

import { isMatchedIgnoredPaths } from "../helpers"

function Seo({
  description = "",
  keywords = "",
  noIndex,
  title: pageTitle,
  lang,
  socialShareImageTwitter,
  socialShareImageOpengraph,
}) {
  const { site } = useStaticQuery(graphql`
    query SeoQuery {
      site {
        siteMetadata {
          title
          defaultLanguage
          description
          keywords
          siteUrl
          companyName
          companyLegalName
          supportPhone
          supportEmail
          socialProfiles {
            url
          }
        }
      }
    }
  `)

  const title = pageTitle || site.siteMetadata.title
  const metaDescription = description || site.siteMetadata.description
  const metaKeywords = `solarisbank, ${keywords || site.siteMetadata.keywords}`
  const searchEngineVisibility = noIndex
    ? {
        name: "robots",
        content: "noindex",
      }
    : {}

  return (
    <IntlContextConsumer>
      {({
        defaultLanguage,
        language: currentLocale,
        languages,
        ignoredPaths,
        originalPath,
        redirectDefaultLanguageToRoot,
      }) => (
        <Helmet
          htmlAttributes={{
            lang: lang || currentLocale,
            itemScope: "",
            itemType: "http://schema.org/WebPage",
          }}
          title={title}
          titleTemplate={pageTitle ? `%s | ${site.siteMetadata.title}` : title}
          meta={[
            {
              itemprop: `name`,
              content: `${title} | ${site.siteMetadata.title}`,
            },
            {
              name: `description`,
              content: metaDescription,
            },
            {
              name: `keywords`,
              content: metaKeywords,
            },
            searchEngineVisibility,
            {
              property: `og:title`,
              content: `${title} | ${site.siteMetadata.title}`,
            },
            {
              property: `og:type`,
              content: `website`,
            },
            {
              property: `og:description`,
              content: metaDescription,
            },
            {
              property: `og:image`,
              content:
                socialShareImageOpengraph ||
                `${site.siteMetadata.siteUrl}/assets/images/share/solarisshareimage_og.png`,
            },
            {
              name: `twitter:card`,
              content: `summary_large_image`,
            },
            {
              name: `twitter:title`,
              content: `${title} | ${site.siteMetadata.title}`,
            },
            {
              name: `twitter:description`,
              content: metaDescription,
            },
            {
              name: `twitter:image`,
              content:
                socialShareImageTwitter ||
                `${site.siteMetadata.siteUrl}/assets/images/share/solarisshareimage_tw.png`,
            },
            {
              name: `google-site-verification`,
              content: `Z8EMHBpzALmgXb0U5RD6l_wgbW2mEBE0KHoBYi764L4`,
            },
            {
              name: `google-site-verification`,
              content: `000nheiTX_51hH4lZRy1RNF2kbM7dJjpqa5oUu_fjtQ`,
            },
            {
              name: `google-site-verification`,
              content: `THeEh43okYZ3V5nGQTidlrb2hDK1lAovGWKpsXUET-U`,
            },
            {
              name: `google-site-verification`,
              content: `c_bJPZbjnURefQMbEDh7LyjO6Kryp7vEd3c79Ju8SzE`,
            },
          ]}
        >
          <link
            rel="preconnect"
            href="https://images.ctfassets.net"
            crossOrigin=""
          />
          <link
            rel="preconnect"
            href="https://solaris.containers.piwik.pro"
            crossOrigin=""
          />
          <link
            rel="canonical"
            href={
              (redirectDefaultLanguageToRoot &&
                defaultLanguage === currentLocale) ||
              isMatchedIgnoredPaths(ignoredPaths, originalPath)
                ? `${site.siteMetadata.siteUrl}${originalPath}`
                : `${site.siteMetadata.siteUrl}/${currentLocale}${originalPath}`
            }
          />
          {!isMatchedIgnoredPaths(ignoredPaths, originalPath) &&
            languages.length > 1 &&
            languages.map((language) => (
              <link
                rel="alternate"
                href={
                  (redirectDefaultLanguageToRoot &&
                    defaultLanguage === language) ||
                  isMatchedIgnoredPaths(ignoredPaths, originalPath)
                    ? `${site.siteMetadata.siteUrl}${originalPath}`
                    : `${site.siteMetadata.siteUrl}/${language}${originalPath}`
                }
                hrefLang={language}
                key={language}
              />
            ))}
          {!isMatchedIgnoredPaths(ignoredPaths, originalPath) &&
            languages.length > 1 && (
              <link
                rel="alternate"
                href={`${site.siteMetadata.siteUrl}${
                  redirectDefaultLanguageToRoot ? `` : `/${defaultLanguage}`
                }${originalPath}`}
                hrefLang="x-default"
              />
            )}
          <script type="application/ld+json">{`
            {
              "@context": "http://schema.org",
              "@type": "Organization",
              "name": "${site.siteMetadata.companyName}",
              "legalName": "${site.siteMetadata.companyLegalName}",
              "url": "${site.siteMetadata.siteUrl}",
              "logo": "${
                site.siteMetadata.siteUrl
              }/assets/images/logos/solaris-signet.png",
              ${
                site.siteMetadata.socialProfiles?.length > 0
                  ? `"sameAs": [
                ${site.siteMetadata.socialProfiles.map(
                  (profile) => `
                  "${profile.url}"`
                )}
              ],`
                  : ""
              }
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "${site.siteMetadata.supportPhone}",
                "contactType": "customer service",
                "email": "${site.siteMetadata.supportEmail}"
              }
            }
          `}</script>
        </Helmet>
      )}
    </IntlContextConsumer>
  )
}

Seo.propTypes = {
  lang: PropTypes.string,
  description: PropTypes.string,
  noIndex: PropTypes.bool,
  keywords: PropTypes.string,
  title: PropTypes.string.isRequired,
}

export default Seo
